import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { ColorPicker } from '@/components/ui/color-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronDown } from 'lucide-react';
import {
  ArrowNoIcon,
  ArrowFilledTriangle1Icon,
  ArrowFilledTriangle2Icon,
  ArrowOpenTriangle1Icon,
  ArrowOpenTriangle2Icon,
  ArrowOpenTriangle3Icon,
  ArrowFilledCircleIcon,
  ArrowFilledSquareIcon
} from '@/components/icons/symbol-icons';
import { cn } from '@/lib/utils';
import { CURATED_FONTS } from '@/theme/fonts.manifest';
import katex from 'katex';
import 'katex/dist/katex.min.css';
// import { usePlotsStore } from '@/store/plots';

interface AnnotationControlsProps {
  plotlyJson?: any;
  onUpdate: (updateType: string, value: any) => void;
}

export function AnnotationControls({ 
  plotlyJson, 
  onUpdate
}: AnnotationControlsProps) {
  // Font family options (curated set)
  const fontFamilies = CURATED_FONTS.map(f => ({ value: f.value, label: f.label }));
  
  // Store helpers are not used directly; all changes go through onUpdate
  const [selectedAnnotationIndex, setSelectedAnnotationIndex] = useState<number | null>(null);
  const [selectedShapeIndex, setSelectedShapeIndex] = useState<number | null>(null);
  const [backgroundBoxState, setBackgroundBoxState] = useState<{[key: number]: boolean}>({});
  const [expandedSections, setExpandedSections] = useState({
    textList: true,
    shapeList: true,
    textProps: true,
    shapeProps: true
  });

  // Get annotations and shapes from plotlyJson
  const annotations = plotlyJson?.layout?.annotations || [];
  const shapes = plotlyJson?.layout?.shapes || [];
  
  // Default colors from theme
  const defaultTextColor = plotlyJson?.layout?.font?.color || '#000000';
  // const defaultLineColor = '#000000';
  
  const selectedAnnotation = selectedAnnotationIndex !== null ? annotations[selectedAnnotationIndex] : null;
  const selectedShape = selectedShapeIndex !== null ? shapes[selectedShapeIndex] : null;
  
  // Sync background box state with actual annotation state
  useEffect(() => {
    if (selectedAnnotation && selectedAnnotationIndex !== null) {
      const hasBackground = !!selectedAnnotation.borderwidth && selectedAnnotation.borderwidth > 0;
      setBackgroundBoxState(prev => ({
        ...prev,
        [selectedAnnotationIndex]: hasBackground
      }));
    }
  }, [selectedAnnotation?.borderwidth, selectedAnnotationIndex]);

  // Line width options for sliders
  // const lineWidthOptions = [0, 0.5, 1, 1.5, 2, 3, 4, 6, 8];

  // Helper to get plot center based on axis type
  const getPlotCenter = useCallback(() => {
    // If no layout, check if we have data to determine reasonable defaults
    if (!plotlyJson?.layout) {
      // Try to get x and y from first data trace
      if (plotlyJson?.data?.[0]) {
        const firstTrace = plotlyJson.data[0];
        const xData = firstTrace.x || [];
        const yData = firstTrace.y || [];
        
        // Get center of data if available
        if (xData.length > 0 && yData.length > 0) {
          const xCenter = typeof xData[0] === 'string' 
            ? xData[Math.floor(xData.length / 2)]
            : (Math.min(...xData) + Math.max(...xData)) / 2;
          const yCenter = typeof yData[0] === 'number'
            ? (Math.min(...yData) + Math.max(...yData)) / 2
            : yData[Math.floor(yData.length / 2)];
          console.log('🎯 Using data center:', { xCenter, yCenter });
          return { x: xCenter, y: yCenter };
        }
      }
      console.log('⚠️ No layout found, using default center');
      return { x: 0.5, y: 0.5 }; // Default to center of paper coordinates
    }
    
    const xaxis = plotlyJson.layout.xaxis || {};
    const yaxis = plotlyJson.layout.yaxis || {};
    
    // Helper to get center for different axis types
    const getAxisCenter = (axis: any, isXAxis: boolean) => {
      const range = axis.range || [0, 1];
      const type = axis.type || 'linear';
      
      switch (type) {
        case 'log':
          // For log scale, use geometric mean
          const logMin = Math.log10(range[0] > 0 ? range[0] : 1);
          const logMax = Math.log10(range[1] > 0 ? range[1] : 10);
          return Math.pow(10, (logMin + logMax) / 2);
        
        case 'date':
          // For date axis, get midpoint timestamp
          const minTime = new Date(range[0]).getTime();
          const maxTime = new Date(range[1]).getTime();
          return new Date((minTime + maxTime) / 2).toISOString();
        
        case 'category':
        case 'multicategory':
          // For categorical, use middle category index
          const categories = axis.categoryarray || plotlyJson?.data?.[0]?.[isXAxis ? 'x' : 'y'] || [];
          if (Array.isArray(categories) && categories.length > 0) {
            return categories[Math.floor(categories.length / 2)];
          }
          return 0;
        
        default: // linear
          return (range[0] + range[1]) / 2;
      }
    };
    
    return {
      x: getAxisCenter(xaxis, true),
      y: getAxisCenter(yaxis, false)
    };
  }, [plotlyJson]);

  // Handle adding text annotation - always use paper coordinates at center
  const handleAddTextAnnotation = useCallback(() => {
    console.log('🚀 handleAddTextAnnotation triggered');
      const newAnnotation = {
      text: 'New Text',
      x: 0.5,  // Center of plot
      y: 0.5,  // Center of plot
      xref: 'paper',
      yref: 'paper',
      xanchor: 'center',
      yanchor: 'middle',
      showarrow: false,
      font: {
        family: plotlyJson?.layout?.font?.family || CURATED_FONTS[0].value,
        size: 16,
        color: defaultTextColor
      },
      _addedFromEditor: true  // Mark as added from editor
      // No background by default - user can enable if needed
    };
    
    console.log('📄 Adding text annotation:', newAnnotation);
    onUpdate('annotation.add', newAnnotation);
    // Select the newly added annotation
    setTimeout(() => {
      setSelectedAnnotationIndex(annotations.length);
      setSelectedShapeIndex(null);
    }, 50);
  }, [onUpdate, annotations.length, plotlyJson, defaultTextColor]);

  // Handle adding arrow - simple arrow at center pointing diagonally
  const handleAddArrow = useCallback(() => {
    console.log('🏹 handleAddArrow triggered');
    const newAnnotation = {
      text: '',  // No text for arrow
      x: 0.6,  // Arrow points to 60% from left
      y: 0.6,  // Arrow points to 60% from bottom
      xref: 'paper',
      yref: 'paper',
      ax: 0.4,  // Arrow starts from 40% from left
      ay: 0.4,  // Arrow starts from 40% from bottom
      axref: 'paper',
      ayref: 'paper',
      showarrow: true,
      arrowhead: 2,
      arrowsize: 1.5,
      arrowwidth: 2,
      arrowcolor: '#000000',
      _addedFromEditor: true  // Mark as added from editor
    };

    console.log('🏹 Adding arrow annotation:', newAnnotation);
    onUpdate('annotation.add', newAnnotation);
    // Select the newly added annotation  
    setTimeout(() => {
      setSelectedAnnotationIndex(annotations.length);
      setSelectedShapeIndex(null);
    }, 50);
  }, [onUpdate, annotations.length]);

  // Handle adding straight line - horizontal line at center
  const handleAddLine = useCallback(() => {
    console.log('📏 handleAddLine triggered');
    const newShape = {
      type: 'line',
      x0: 0.3,  // Line starts at 30% from left
      y0: 0.5,  // Center vertically
      x1: 0.7,  // Line ends at 70% from left
      y1: 0.5,  // Center vertically
      xref: 'paper',
      yref: 'paper',
      line: {
        color: '#000000',
        width: 2
      },
      _addedFromEditor: true  // Mark as added from editor
    };

    console.log('📏 Adding line shape:', newShape);
    onUpdate('overlay.add', newShape);
    // Select the newly added shape
    setTimeout(() => {
      setSelectedShapeIndex(shapes.length);
      setSelectedAnnotationIndex(null);
    }, 50);
  }, [onUpdate, shapes.length]);

  // Handle adding shapes - use paper coordinates
  // Future: reserved for non-line shapes; currently unused
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAddShape = useCallback((type: 'circle' | 'rect' | 'bracket') => {
    const center = getPlotCenter();
    const xRange = plotlyJson?.layout?.xaxis?.range || [0, 1];
    const yRange = plotlyJson?.layout?.yaxis?.range || [0, 1];
    // const xaxis = plotlyJson?.layout?.xaxis || {}; // kept for future categorical width logic
    
    if (type === 'circle') {
      // Arrow implemented as annotation with showarrow
      const newAnnotation = {
        text: '',
        x: center.x,
        y: center.y,
        xref: 'x',
        yref: 'y',
        ax: -40, // Default vector 40 pixels left
        ay: -40, // and 40 pixels up (negative for up)
        axref: 'pixel',
        ayref: 'pixel',
        xanchor: 'center',
        yanchor: 'middle',
        showarrow: true,
        arrowhead: 2,
        arrowsize: 1,
        arrowwidth: 2,
        arrowcolor: '#000000',
        _addedFromEditor: true  // Mark as added from editor
      };

      onUpdate('annotation.add', newAnnotation);
      setSelectedAnnotationIndex(annotations.length);
      setSelectedShapeIndex(null);
      
    } else if (type === 'bracket') {
      // Significance bracket stored as a single path shape so it can be moved/resized as one unit
      const widthPaper = 0.3; // 30% of plot width
      const xStart = 0.5 - widthPaper / 2;
      const xEnd = 0.5 + widthPaper / 2;
      const yTop = 0.88;      // near top of plot
      const height = 0.08;    // 8% of plot height
      const yLow = yTop - height;

      const bracketShape = {
        type: 'path' as const,
        path: `M ${xStart},${yLow} L ${xStart},${yTop} L ${xEnd},${yTop} L ${xEnd},${yLow}`,
        xref: 'paper' as const,
        yref: 'paper' as const,
        layer: 'above' as const,
        line: { color: '#000000', width: 1.5 },
        lumiBracket: {
          x0: xStart,
          x1: xEnd,
          yTop,
          yLow
        },
        _addedFromEditor: true  // Mark as added from editor
      };

      const newAnnotation = {
        text: '*',
        x: 0.5,
        y: Math.min(0.98, yTop + 0.02),
        xref: 'paper',
        yref: 'paper',
        showarrow: false,
        font: { size: 16, color: '#000000' },
        xanchor: 'center',
        yanchor: 'bottom',
        _addedFromEditor: true  // Mark as added from editor
      };

      // Apply shape and label in a single batched update so the bracket persists
      const operations = [
        { type: 'overlay.add', value: bracketShape },
        { type: 'annotation.add', value: newAnnotation }
      ];

      onUpdate('batch.apply', operations);

      setTimeout(() => {
        setSelectedShapeIndex(shapes.length);
        setSelectedAnnotationIndex(null);
      }, 50);
      
    } else {
      // Rectangle (since not circle or bracket)
      const sizeX = (xRange[1] - xRange[0]) * 0.15;
      const sizeY = (yRange[1] - yRange[0]) * 0.15;

      const newShape: any = {
        type: 'rect' as const,
        xref: 'x',
        yref: 'y',
        x0: center.x - sizeX / 2,
        y0: center.y - sizeY / 2,
        x1: center.x + sizeX / 2,
        y1: center.y + sizeY / 2,
        line: {
          color: '#000000',
          width: 2
        },
        fillcolor: 'rgba(0, 0, 0, 0)',
        opacity: 1,
        _addedFromEditor: true  // Mark as added from editor
      };

      onUpdate('overlay.add', newShape);
      setSelectedShapeIndex(shapes.length);
      setSelectedAnnotationIndex(null);
    }
  }, [getPlotCenter, onUpdate, annotations.length, shapes.length, plotlyJson]);

  // Update annotation - properly handle font properties
  const updateSelectedAnnotation = useCallback((property: string, value: any) => {
    if (selectedAnnotationIndex === null) return;
    
    console.log('🔧 updateSelectedAnnotation:', { index: selectedAnnotationIndex, property, value });
    
    // Send the update with the correct path
    onUpdate(`annotation.update.${selectedAnnotationIndex}.${property}`, value);
  }, [selectedAnnotationIndex, onUpdate]);

  // Atomic update for multiple annotation properties at once
  const updateAnnotationAtomic = useCallback((updates: Record<string, any>) => {
    if (selectedAnnotationIndex === null) return;
    
    console.log('⚛️ Atomic annotation update:', { index: selectedAnnotationIndex, updates });
    
    // Send all updates as a single batch
    onUpdate(`annotation.update.${selectedAnnotationIndex}.batch`, updates);
  }, [selectedAnnotationIndex, onUpdate]);

  // Update shape - handle nested properties correctly
  const updateSelectedShape = useCallback((property: string, value: any) => {
    if (selectedShapeIndex === null) return;
    
    console.log('🔧 updateSelectedShape:', { index: selectedShapeIndex, property, value });
    
    // Send the update with the correct path
    onUpdate(`overlay.update.${selectedShapeIndex}.${property}`, value);
  }, [selectedShapeIndex, onUpdate]);

  // Remove annotation
  const removeSelectedAnnotation = useCallback((index: number) => {
    onUpdate(`annotation.remove.${index}`, null);
    if (selectedAnnotationIndex === index) {
      setSelectedAnnotationIndex(null);
    } else if (selectedAnnotationIndex !== null && selectedAnnotationIndex > index) {
      setSelectedAnnotationIndex(selectedAnnotationIndex - 1);
    }
  }, [selectedAnnotationIndex, onUpdate]);

  // Remove shape
  const removeSelectedShape = useCallback((index: number) => {
    onUpdate(`overlay.remove.${index}`, null);
    if (selectedShapeIndex === index) {
      setSelectedShapeIndex(null);
    } else if (selectedShapeIndex !== null && selectedShapeIndex > index) {
      setSelectedShapeIndex(selectedShapeIndex - 1);
    }
  }, [selectedShapeIndex, onUpdate]);

  // Remove all annotations and shapes added from editor
  const removeAllEditorItems = useCallback(() => {
    // Collect indices of annotations to remove
    const annotationIndicesToRemove: number[] = [];
    annotations.forEach((anno: any, index: number) => {
      if (anno._addedFromEditor === true) {
        annotationIndicesToRemove.push(index);
      }
    });

    // Collect indices of shapes to remove
    const shapeIndicesToRemove: number[] = [];
    shapes.forEach((shape: any, index: number) => {
      if (shape._addedFromEditor === true) {
        shapeIndicesToRemove.push(index);
      }
    });

    // Build batch operations: remove in reverse order to avoid index shifting issues
    const operations: Array<{ type: string; value: any }> = [];
    
    // Remove annotations in reverse order (highest index first)
    // Sort descending to ensure highest indices are removed first
    [...annotationIndicesToRemove].sort((a, b) => b - a).forEach((index: number) => {
      operations.push({ type: `annotation.remove.${index}`, value: null });
    });

    // Remove shapes in reverse order (highest index first)
    // Sort descending to ensure highest indices are removed first
    [...shapeIndicesToRemove].sort((a, b) => b - a).forEach((index: number) => {
      operations.push({ type: `overlay.remove.${index}`, value: null });
    });

    // Apply all removals atomically via batch update
    if (operations.length > 0) {
      onUpdate('batch.apply', operations);
    }

    // Clear selections if they were removed
    if (selectedAnnotationIndex !== null && annotationIndicesToRemove.includes(selectedAnnotationIndex)) {
      setSelectedAnnotationIndex(null);
    }
    if (selectedShapeIndex !== null && shapeIndicesToRemove.includes(selectedShapeIndex)) {
      setSelectedShapeIndex(null);
    }
  }, [annotations, shapes, selectedAnnotationIndex, selectedShapeIndex, onUpdate]);

  // Check if there are any editor-added items
  const hasEditorItems = useMemo(() => {
    const hasEditorAnnotations = annotations.some((anno: any) => anno._addedFromEditor === true);
    const hasEditorShapes = shapes.some((shape: any) => shape._addedFromEditor === true);
    return hasEditorAnnotations || hasEditorShapes;
  }, [annotations, shapes]);

  // Insert text style tags at cursor or wrap selected text
  const insertTextStyle = useCallback((style: 'bold' | 'italic' | 'underline' | 'superscript' | 'subscript') => {
    if (!selectedAnnotation) return;
    
    let text = selectedAnnotation.text || '';
    
    // Don't apply styles to LaTeX
    if (text.includes('$')) return;
    
    // Define the tag pairs
    const tags = {
      bold: ['<b>', '</b>'],
      italic: ['<i>', '</i>'],
      underline: ['<u>', '</u>'],
      superscript: ['<sup>', '</sup>'],
      subscript: ['<sub>', '</sub>']
    };
    
    const [openTag, closeTag] = tags[style];
    
    // Simply wrap the existing text with the tag if it doesn't have it
    // or provide example text if empty
    if (!text) {
      text = `${openTag}text${closeTag}`;
    } else if (!text.includes(openTag)) {
      // Check if we should wrap the whole text or add example
      const hasAnyTags = Object.values(tags).some(([open]) => text.includes(open));
      if (!hasAnyTags) {
        // No tags present, wrap the whole text
        text = `${openTag}${text}${closeTag}`;
      } else {
        // Has other tags, add example at the end
        text = `${text} ${openTag}text${closeTag}`;
      }
    }
    
    updateSelectedAnnotation('text', text);
  }, [selectedAnnotation, updateSelectedAnnotation]);

  // Render LaTeX preview
  const renderLatexPreview = (text: string): string => {
    try {
      // Check if it's LaTeX (has $ or \)
      if (!text || (!text.includes('$') && !text.includes('\\'))) {
        return text;
      }
      
      // Extract LaTeX content between $ signs
      const latexMatch = text.match(/\$([^$]+)\$/);
      if (latexMatch) {
        return katex.renderToString(latexMatch[1], {
          throwOnError: false,
          displayMode: false
        });
      }
      
      // If no $ signs, try to render the whole string as LaTeX
      return katex.renderToString(text, {
        throwOnError: false,
        displayMode: false
      });
    } catch (e) {
      return '<span style="color: red;">Invalid LaTeX</span>';
    }
  };

  // Check if text is LaTeX
  const isLatex = (text: string) => {
    return text && (text.includes('$') || text.includes('\\'));
  };

  // Treat any annotation with showarrow=true as an Arrow (even if it has text)
  // This prevents arrow options from hiding when users add a label to the arrow
  const isArrow = (annotation: any) => {
    return !!(annotation && annotation.showarrow);
  };

  // Parse significance bracket shape path (M x0,yLow L x0,yTop L x1,yTop L x1,yLow)
  const parseBracketPath = (pathStr: string | undefined) => {
    try {
      if (!pathStr || typeof pathStr !== 'string') return null as any;
      const nums = (pathStr.match(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi) || []).map(parseFloat);
      if (nums.length < 8) return null as any;
      const x0 = nums[0];
      const yLow = nums[1];
      const yTop = nums[3];
      const x1 = nums[4];
      const height = yTop - yLow;
      const xCenter = (x0 + x1) / 2;
      return { x0, x1, yTop, yLow, height, xCenter };
    } catch { return null as any; }
  };

  // Find associated label annotation for a given bracket shape (by proximity)
  const findBracketAnnotationIndex = (shape: any): number | null => {
    try {
      const info = parseBracketPath(shape?.path);
      if (!info) return null;
      const { xCenter, yTop } = info;
      const annos:any[] = Array.isArray(plotlyJson?.layout?.annotations) ? plotlyJson.layout.annotations : [];
      const shapeXref = shape?.xref || 'paper';
      const shapeYref = shape?.yref || 'paper';
      const getAxisRangeSpan = (axisRef: string) => {
        if (axisRef === 'paper') return 1;
        const axisKey = axisRef.endsWith('axis') ? axisRef : `${axisRef}axis`;
        const axis = plotlyJson?.layout?.[axisKey];
        if (Array.isArray(axis?.range)) {
          const span = Math.abs(axis.range[1] - axis.range[0]);
          return span || 1;
        }
        return 1;
      };
      const ySpan = getAxisRangeSpan(shapeYref);
      const expectedY = shapeYref === 'paper'
        ? Math.min(0.98, yTop + 0.02)
        : yTop + ySpan * 0.02;

      let bestIdx: number | null = null;
      let bestScore = Infinity;
      for (let i = 0; i < annos.length; i++) {
        const a = annos[i];
        if (!a || a.showarrow) continue;
        if ((a.xref || 'paper') !== shapeXref) continue;
        if ((a.yref || 'paper') !== shapeYref) continue;
        const dx = Math.abs((a.x ?? 0) - xCenter);
        const dy = Math.abs((a.y ?? 0) - expectedY);
        const score = dx + dy * 1.5;
        if (score < bestScore) {
          bestScore = score;
          bestIdx = i;
        }
      }
      return bestIdx;
    } catch { return null; }
  };

  // Helper text for selected items
  const getHelpText = () => {
    if (selectedAnnotation || selectedShape) {
      return "Drag to move. Use handles to resize.";
    }
    return null;
  };

  return (
    <div className="space-y-4 max-w-full">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-lg font-semibold">Annotations</h4>
        {hasEditorItems && (
          <Button
            variant="outline"
            size="sm"
            onClick={removeAllEditorItems}
            className="text-xs"
          >
            Remove All
          </Button>
        )}
      </div>

      {/* Help text when item is selected */}
      {getHelpText() && (
        <div className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded flex items-center gap-1">
          {getHelpText()}
        </div>
      )}

      <div className="space-y-3">
          {/* Add buttons */}
          <div className="grid grid-cols-4 gap-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={handleAddTextAnnotation}
            >
              Text
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={handleAddArrow}
            >
              Arrow
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={handleAddLine}
            >
              Line
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => handleAddShape('bracket')}
            >
              Bracket
            </Button>
          </div>

          {/* Text Annotations List */}
          {annotations.length > 0 && (
            <div className="space-y-2">
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setExpandedSections(prev => ({ ...prev, textList: !prev.textList }))}
              >
                <Label className="text-sm text-muted-foreground">
                  Text annotations ({annotations.filter((a: any) => !isArrow(a)).length})
                </Label>
              </div>
              
              {expandedSections.textList && (
                <div className="space-y-1 max-w-full overflow-x-auto">
                  {annotations.filter((anno: any) => !isArrow(anno)).map((anno: any) => {
                    const actualIndex = annotations.indexOf(anno);
                    return (
                    <div
                      key={actualIndex}
                      className={cn(
                        "p-2 border rounded cursor-pointer transition-colors overflow-hidden",
                        selectedAnnotationIndex === actualIndex ? "border-primary bg-accent" : "hover:bg-accent/50"
                      )}
                      onClick={() => {
                        setSelectedAnnotationIndex(actualIndex);
                        setSelectedShapeIndex(null);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1 min-w-0 overflow-x-auto">
                          <span className="block text-sm whitespace-nowrap">
                            {isLatex(anno.text) ? 'Formula' : (anno.text ? 
                              anno.text
                                .replace(/<\/?b>/g, '')
                                .replace(/<\/?i>/g, '')
                                .replace(/<\/?u>/g, '')
                                .replace(/<\/?sup>/g, '')
                                .replace(/<\/?sub>/g, '')
                                .replace(/<\/?[^>]+>/g, '') // Remove any other HTML tags
                              : 'Text')}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeSelectedAnnotation(actualIndex);
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Selected Annotation Text Properties (shown for both text and arrow annotations) */}
          {selectedAnnotation && selectedAnnotationIndex !== null && (
            <div className="space-y-3 pt-3 border-t">
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setExpandedSections(prev => ({ ...prev, textProps: !prev.textProps }))}
              >
                <Label className="text-xs font-medium">Properties</Label>
                <ChevronDown 
                  className={cn(
                    "h-4 w-4 transition-transform duration-200",
                    expandedSections.textProps ? "rotate-180" : ""
                  )} 
                />
              </div>

              {expandedSections.textProps && (
                <>
                  {/* Text Content */}
                  <div>
                    <Label className="text-xs">
                      {isLatex(selectedAnnotation.text) ? 'LaTeX Formula' : 'Text Content'}
                    </Label>
                    {isLatex(selectedAnnotation.text) ? (
                      <>
                        <Textarea
                          className="mt-1 font-mono text-xs"
                          rows={2}
                          value={selectedAnnotation.text}
                          onChange={(e) => updateSelectedAnnotation('text', e.target.value)}
                          placeholder="$x^2 + y^2 = z^2$"
                        />
                        <div className="mt-2 p-2 bg-muted rounded text-sm">
                          <div dangerouslySetInnerHTML={{ __html: renderLatexPreview(selectedAnnotation.text) }} />
                        </div>
                      </>
                    ) : (
                      <>
                        <Textarea
                          className="mt-1 font-mono text-sm"
                          rows={3}
                          value={selectedAnnotation.text || ''}
                          onChange={(e) => updateSelectedAnnotation('text', e.target.value)}
                          placeholder="Enter text or HTML: <b>bold</b>, <i>italic</i>, <u>underline</u>, <sup>super</sup>, <sub>sub</sub>"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Mix styles: Normal {'<b>bold</b>'} {'<i>italic</i>'} {'<u>underline</u>'} x{'<sup>2</sup>'} H{'<sub>2</sub>'}O
                        </p>
                        <div className="space-y-2 mt-2">
                          {/* Text Style and Alignment - Combined in one row */}
                          <div>
                            <Label className="text-xs mb-1 block">Text Style & Alignment</Label>
                            <div className="flex gap-1">
                              <Button
                                variant={selectedAnnotation.text?.includes('<b>') ? 'default' : 'outline'}
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => insertTextStyle('bold')}
                                title="Insert Bold Tags"
                              >
                                Bold
                              </Button>
                              <Button
                                variant={selectedAnnotation.text?.includes('<i>') ? 'default' : 'outline'}
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => insertTextStyle('italic')}
                                title="Insert Italic Tags"
                              >
                                Italic
                              </Button>
                              <Button
                                variant={selectedAnnotation.text?.includes('<sup>') ? 'default' : 'outline'}
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => insertTextStyle('superscript')}
                                title="Insert Superscript Tags"
                              >
                                Sup
                              </Button>
                              <Button
                                variant={selectedAnnotation.text?.includes('<sub>') ? 'default' : 'outline'}
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => insertTextStyle('subscript')}
                                title="Insert Subscript Tags"
                              >
                                Sub
                              </Button>
                              <Button
                                variant={selectedAnnotation.text?.includes('<u>') ? 'default' : 'outline'}
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => insertTextStyle('underline')}
                                title="Insert Underline Tags"
                              >
                                Underline
                              </Button>
                              <div className="w-px h-7 bg-border mx-1" />
                              <Button
                                variant={selectedAnnotation.xanchor === 'left' ? 'default' : 'outline'}
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => updateSelectedAnnotation('xanchor', 'left')}
                                title="Align Left"
                              >
                                Left
                              </Button>
                              <Button
                                variant={selectedAnnotation.xanchor === 'center' ? 'default' : 'outline'}
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => updateSelectedAnnotation('xanchor', 'center')}
                                title="Align Center"
                              >
                                Center
                              </Button>
                              <Button
                                variant={selectedAnnotation.xanchor === 'right' ? 'default' : 'outline'}
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => updateSelectedAnnotation('xanchor', 'right')}
                                title="Align Right"
                              >
                                Right
                              </Button>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Font Family */}
                  <div>
                    <Label className="text-xs">Font Family</Label>
                    <Select 
                      value={selectedAnnotation.font?.family || plotlyJson?.layout?.font?.family || CURATED_FONTS[0].value} 
                      onValueChange={(value) => updateSelectedAnnotation('font.family', value)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue>
                          {(() => {
                            const currentFont = selectedAnnotation.font?.family || plotlyJson?.layout?.font?.family || CURATED_FONTS[0].value;
                            const fontLabel = fontFamilies.find(f => f.value === currentFont)?.label || CURATED_FONTS[0].label;
                            return fontLabel;
                          })()}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                    {fontFamilies.map(font => (
                      <SelectItem key={font.value} value={font.value}>
                        <span data-font-name={font.label}>{font.label}</span>
                      </SelectItem>
                    ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Font Size */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs">Font Size</Label>
                      <span className="text-xs text-muted-foreground">{selectedAnnotation.font?.size || 14}px</span>
                    </div>
                    <Slider
                      value={[selectedAnnotation.font?.size || 14]}
                      onValueChange={([value]) => updateSelectedAnnotation('font.size', value)}
                      min={8}
                      max={64}
                      step={1}
                      className="w-full"
                    />
                  </div>

                  {/* Font Color */}
                  <ColorPicker
                    label="Font Color"
                    value={selectedAnnotation.font?.color || '#000000'}
                    onChange={(color) => updateSelectedAnnotation('font.color', color)}
                    includeOpacity={false}
                  />

                  {/* Background Box */}
                  <div className="flex items-center justify-between">
                    <Label htmlFor="bgbox" className="text-sm">Background Box</Label>
                    <Switch
                      id="bgbox"
                      checked={
                        backgroundBoxState[selectedAnnotationIndex] !== undefined 
                          ? backgroundBoxState[selectedAnnotationIndex]
                          : (!!selectedAnnotation.borderwidth && selectedAnnotation.borderwidth > 0)
                      }
                      onCheckedChange={(checked) => {
                        console.log('Background box toggle:', checked, 'current borderwidth:', selectedAnnotation.borderwidth, 'index:', selectedAnnotationIndex);
                        
                        // Update local state immediately for responsive UI
                        setBackgroundBoxState(prev => ({
                          ...prev,
                          [selectedAnnotationIndex]: checked
                        }));
                        
                        if (checked) {
                          // Turn on background box - atomic update
                          updateAnnotationAtomic({
                            borderwidth: 1,
                            borderpad: 4,
                            bgcolor: 'rgba(255, 255, 255, 0.8)',
                            bordercolor: '#000000'
                          });
                        } else {
                          // Turn off background box - atomic update
                          console.log('🔴 Turning OFF background box atomically');
                          updateAnnotationAtomic({
                            borderwidth: 0,
                            borderpad: 0,
                            bgcolor: 'rgba(0,0,0,0)',
                            bordercolor: 'rgba(0,0,0,0)'
                          });
                        }
                      }}
                    />
                  </div>

                  {/* Background Box Properties */}
                  {(backgroundBoxState[selectedAnnotationIndex] !== undefined 
                    ? backgroundBoxState[selectedAnnotationIndex]
                    : (!!selectedAnnotation.borderwidth && selectedAnnotation.borderwidth > 0)) && (
                    <>
                      <ColorPicker
                        label="Box Color"
                        value={selectedAnnotation.bgcolor || 'rgba(255, 255, 255, 0.8)'}
                        onChange={(color) => {
                          // Guard: only update if background box is ON
                          const isOn = backgroundBoxState[selectedAnnotationIndex] !== undefined 
                            ? backgroundBoxState[selectedAnnotationIndex]
                            : (!!selectedAnnotation.borderwidth && selectedAnnotation.borderwidth > 0);
                          if (!isOn) return;
                          updateSelectedAnnotation('bgcolor', color);
                        }}
                        includeOpacity={true}
                      />
                      
                      <ColorPicker
                        label="Border Color"
                        value={selectedAnnotation.bordercolor || '#000000'}
                        onChange={(color) => {
                          // Guard: only update if background box is ON
                          const isOn = backgroundBoxState[selectedAnnotationIndex] !== undefined 
                            ? backgroundBoxState[selectedAnnotationIndex]
                            : (!!selectedAnnotation.borderwidth && selectedAnnotation.borderwidth > 0);
                          if (!isOn) return;
                          updateSelectedAnnotation('bordercolor', color);
                        }}
                        includeOpacity={false}
                      />
                      
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <Label className="text-xs">Border Width</Label>
                          <span className="text-xs text-muted-foreground">
                            {selectedAnnotation.borderwidth || 1}px
                          </span>
                        </div>
                        <Slider
                          value={[selectedAnnotation.borderwidth || 1]}
                          onValueChange={([value]) => updateSelectedAnnotation('borderwidth', value)}
                          min={0}
                          max={10}
                          step={0.5}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>0px</span>
                          <span>{selectedAnnotation.borderwidth || 1}px</span>
                          <span>10px</span>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {!selectedAnnotation && annotations.length === 0 && (
            <div className="text-center py-4 text-sm text-muted-foreground">
              Add a text annotation to get started
            </div>
          )}

          {/* Combined Shapes and Arrow Annotations List */}
          {(shapes.length > 0 || annotations.some(isArrow)) && (
            <div className="space-y-2">
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setExpandedSections(prev => ({ ...prev, shapeList: !prev.shapeList }))}
              >
                <Label className="text-sm text-muted-foreground">
                  Shapes & arrows ({shapes.length + annotations.filter(isArrow).length})
                </Label>
              </div>
              
              {expandedSections.shapeList && (
                <div className="space-y-1 max-w-full overflow-x-auto">
                  {/* Arrow annotations */}
                  {annotations.map((anno: any, index: number) => {
                    if (!isArrow(anno)) return null as any;
                    return (
                      <div
                        key={`anno-${index}`}
                        className={cn(
                          "p-2 border rounded cursor-pointer transition-colors overflow-hidden",
                          selectedAnnotationIndex === index ? "border-primary bg-accent" : "hover:bg-accent/50"
                        )}
                        onClick={() => {
                          setSelectedAnnotationIndex(index);
                          setSelectedShapeIndex(null);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1 min-w-0 overflow-x-auto">
                            <span className="block text-sm whitespace-nowrap">Arrow</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeSelectedAnnotation(index);
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Shapes */}
                  {shapes.map((shape: any, index: number) => (
                    <div
                      key={`shape-${index}`}
                      className={cn(
                        "p-2 border rounded cursor-pointer transition-colors overflow-hidden",
                        selectedShapeIndex === index ? "border-primary bg-accent" : "hover:bg-accent/50"
                      )}
                      onClick={() => {
                        setSelectedShapeIndex(index);
                        setSelectedAnnotationIndex(null);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1 min-w-0 overflow-x-auto">
                          <span className="block text-sm capitalize whitespace-nowrap">
                            {shape.type === 'path' ? 'Sig Bracket' : shape.type === 'rect' ? 'Rectangle' : shape.type === 'line' ? 'Line' : 'Circle'}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeSelectedShape(index);
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Selected Arrow Properties */}
          {selectedAnnotation && selectedAnnotationIndex !== null && isArrow(selectedAnnotation) && (
            <div className="space-y-3 pt-3 border-t">
              <Label className="text-xs font-medium">Arrow Properties</Label>
              <span className="hidden" aria-hidden="true"></span>
              
              <ColorPicker
                label="Arrow Color"
                value={selectedAnnotation.arrowcolor || '#000000'}
                onChange={(color) => updateSelectedAnnotation('arrowcolor', color)}
                includeOpacity={false}
              />
              
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs">Arrow Width</Label>
                  <span className="text-xs text-muted-foreground">
                    {selectedAnnotation.arrowwidth || 2}px
                  </span>
                </div>
                <Slider
                  value={[selectedAnnotation.arrowwidth || 2]}
                  onValueChange={([value]) => updateSelectedAnnotation('arrowwidth', value)}
                  min={0}
                  max={8}
                  step={0.5}
                  className="w-full"
                />
              </div>

              {/* Arrow Head */}
              <div>
                <Label className="text-xs">Arrow Head Size</Label>
                <div className="mt-1">
                  <Slider
                    value={[selectedAnnotation.arrowsize || 1]}
                    onValueChange={([value]) => updateSelectedAnnotation('arrowsize', value)}
                    min={0.3}
                    max={2}
                    step={0.1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>Small</span>
                    <span>{selectedAnnotation.arrowsize || 1}</span>
                    <span>Large</span>
                  </div>
                </div>
              </div>

              {/* Arrow Head Style */}
              <div>
                <Label className="text-xs">Arrow Head Style</Label>
                <Select 
                  value={String(selectedAnnotation.arrowhead !== undefined && selectedAnnotation.arrowhead !== null ? selectedAnnotation.arrowhead : 1)}
                  onValueChange={(value) => {
                    const intValue = parseInt(value);
                    // When arrowhead is 0 (No Arrow), also set showarrow to false
                    // When arrowhead is not 0, ensure showarrow is true
                    if (intValue === 0) {
                      updateAnnotationAtomic({
                        arrowhead: 0,
                        showarrow: false
                      });
                    } else {
                      updateAnnotationAtomic({
                        arrowhead: intValue,
                        showarrow: true
                      });
                    }
                  }}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue>
                      {(() => {
                        const arrowheadValue: number = (selectedAnnotation.arrowhead !== undefined && selectedAnnotation.arrowhead !== null)
                          ? Number(selectedAnnotation.arrowhead)
                          : 1;
                        // Plotly annotation arrowhead values (actual Plotly styles)
                        const iconMap: Record<number, { Icon: React.ComponentType<{ className?: string; size?: number }>, label: string }> = {
                          0: { Icon: ArrowNoIcon, label: 'No Arrow' },
                          1: { Icon: ArrowFilledTriangle1Icon, label: 'Filled Triangle 1' },
                          2: { Icon: ArrowFilledTriangle2Icon, label: 'Filled Triangle 2' },
                          3: { Icon: ArrowOpenTriangle1Icon, label: 'Open Triangle 1' },
                          4: { Icon: ArrowOpenTriangle2Icon, label: 'Open Triangle 2' },
                          5: { Icon: ArrowOpenTriangle3Icon, label: 'Open Triangle 3' },
                          6: { Icon: ArrowFilledCircleIcon, label: 'Filled Circle' },
                          7: { Icon: ArrowFilledSquareIcon, label: 'Filled Square' }
                        };
                        const selected = iconMap[arrowheadValue] ?? iconMap[1];
                        return (
                          <div className="flex items-center gap-2">
                            <selected.Icon size={16} className="text-foreground" />
                            <span>{selected.label}</span>
                          </div>
                        );
                      })()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">
                      <div className="flex items-center gap-2">
                        <ArrowNoIcon size={16} className="text-foreground" />
                        <span>No Arrow</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="1">
                      <div className="flex items-center gap-2">
                        <ArrowFilledTriangle1Icon size={16} className="text-foreground" />
                        <span>Filled Triangle 1</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="2">
                      <div className="flex items-center gap-2">
                        <ArrowFilledTriangle2Icon size={16} className="text-foreground" />
                        <span>Filled Triangle 2</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="3">
                      <div className="flex items-center gap-2">
                        <ArrowOpenTriangle1Icon size={16} className="text-foreground" />
                        <span>Open Triangle 1</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="4">
                      <div className="flex items-center gap-2">
                        <ArrowOpenTriangle2Icon size={16} className="text-foreground" />
                        <span>Open Triangle 2</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="5">
                      <div className="flex items-center gap-2">
                        <ArrowOpenTriangle3Icon size={16} className="text-foreground" />
                        <span>Open Triangle 3</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="6">
                      <div className="flex items-center gap-2">
                        <ArrowFilledCircleIcon size={16} className="text-foreground" />
                        <span>Filled Circle</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="7">
                      <div className="flex items-center gap-2">
                        <ArrowFilledSquareIcon size={16} className="text-foreground" />
                        <span>Filled Square</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Selected Shape Properties */}
          {selectedShape && selectedShapeIndex !== null && (
            <div className="space-y-3 pt-3 border-t">
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setExpandedSections(prev => ({ ...prev, shapeProps: !prev.shapeProps }))}
              >
                <Label className="text-xs font-medium">Shape Properties</Label>
                <ChevronDown 
                  className={cn(
                    "h-4 w-4 transition-transform duration-200",
                    expandedSections.shapeProps ? "rotate-180" : ""
                  )} 
                />
              </div>

              {expandedSections.shapeProps && (
                <>
                  {/* Sig Bracket specific controls */}
                  {selectedShape.type === 'path' && selectedShape.path && (() => {
                    const info = parseBracketPath(selectedShape.path);
                    if (!info) return null;

                    const shapeXref = selectedShape?.xref || 'paper';
                    const shapeYref = selectedShape?.yref || 'paper';

                    const clamp01 = (val: number) => Math.max(0, Math.min(1, val));
                    const widthPctActual = Math.round((info.x1 - info.x0) * 100);
                    const heightPctActual = Math.round((info.yTop - info.yLow) * 100);
                    const widthSliderValue = Math.max(5, Math.min(90, widthPctActual));
                    const heightSliderValue = Math.max(1, Math.min(30, heightPctActual));

                    const buildPath = (data: typeof info) => `M ${data.x0},${data.yLow} L ${data.x0},${data.yTop} L ${data.x1},${data.yTop} L ${data.x1},${data.yLow}`;

                    const applyGeometryUpdate = (data: typeof info) => {
                      if (selectedShapeIndex === null) return;
                      const newPath = buildPath(data);
                      const ops: Array<{ type: string; value: any }> = [
                        { type: `overlay.update.${selectedShapeIndex}.path`, value: newPath },
                        {
                          type: `overlay.update.${selectedShapeIndex}.lumiBracket`,
                          value: {
                            x0: data.x0,
                            x1: data.x1,
                            yTop: data.yTop,
                            yLow: data.yLow
                          }
                        }
                      ];

                      const idx = findBracketAnnotationIndex({ ...selectedShape, path: newPath });
                      if (idx !== null && idx !== undefined) {
                        const layout = plotlyJson?.layout || {};
                        const axisKey = shapeYref === 'paper' ? null : `${shapeYref}axis`;
                        const axisSpan = axisKey && Array.isArray((layout as any)[axisKey]?.range)
                          ? Math.abs((layout as any)[axisKey].range[1] - (layout as any)[axisKey].range[0]) || 1
                          : 1;
                        const yOffset = shapeYref === 'paper' ? 0.02 : 0.02 * axisSpan;
                        const newY = shapeYref === 'paper' ? Math.min(0.98, data.yTop + yOffset) : data.yTop + yOffset;
                        ops.push({
                          type: `annotation.update.${idx}.batch`,
                          value: {
                            x: data.xCenter,
                            y: newY
                          }
                        });
                      }

                      onUpdate('batch.apply', ops);
                    };

                    return (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-xs">Bracket Label</Label>
                          <Input
                            type="text"
                            placeholder="*"
                            defaultValue={(() => {
                              const idx = findBracketAnnotationIndex(selectedShape);
                              const a = (idx !== null && idx !== undefined) ? plotlyJson?.layout?.annotations?.[idx] : null;
                              return (a?.text ?? '*') as any;
                            })()}
                            className="h-8"
                            onBlur={(e) => {
                              const idx = findBracketAnnotationIndex(selectedShape);
                              const text = e.currentTarget.value || '*';
                              if (idx === null || idx === undefined) {
                                onUpdate('annotation.add', {
                                  text,
                                  x: info.xCenter,
                                  y: shapeYref === 'paper' ? Math.min(0.98, info.yTop + 0.02) : info.yTop + 0.02,
                                  xref: shapeXref,
                                  yref: shapeYref,
                                  showarrow: false,
                                  xanchor: 'center',
                                  yanchor: 'bottom'
                                });
                              } else {
                                onUpdate(`annotation.update.${idx}.text`, text);
                              }
                            }}
                          />
                        </div>

                        <div className="space-y-3">
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <Label className="text-xs">Width</Label>
                              <span className="text-xs text-muted-foreground">{widthSliderValue}%</span>
                            </div>
                            <Slider
                              value={[widthSliderValue]}
                              min={5}
                              max={90}
                              step={1}
                              className="w-full"
                              onValueChange={([pct]) => {
                                const newWidth = pct / 100;
                                const half = newWidth / 2;
                                const x0 = clamp01(info.xCenter - half);
                                const x1 = clamp01(info.xCenter + half);
                                if (x1 - x0 < 0.01) return;
                                const updated = {
                                  ...info,
                                  x0,
                                  x1,
                                  xCenter: (x0 + x1) / 2,
                                  height: info.yTop - info.yLow
                                };
                                applyGeometryUpdate(updated);
                              }}
                            />
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <Label className="text-xs">Height</Label>
                              <span className="text-xs text-muted-foreground">{heightSliderValue}%</span>
                            </div>
                            <Slider
                              value={[heightSliderValue]}
                              min={1}
                              max={30}
                              step={1}
                              className="w-full"
                              onValueChange={([pct]) => {
                                const newHeight = pct / 100;
                                const yTop = clamp01(info.yTop);
                                const yLow = clamp01(yTop - newHeight);
                                if (yTop - yLow < 0.01) return;
                                const updated = {
                                  ...info,
                                  yTop,
                                  yLow,
                                  height: yTop - yLow
                                };
                                applyGeometryUpdate(updated);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Line Color */}
                  <ColorPicker
                    label="Line Color"
                    value={selectedShape.line?.color || '#000000'}
                    onChange={(color) => updateSelectedShape('line.color', color)}
                    includeOpacity={false}
                  />

                  {/* Line Width */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-xs">Line Width</Label>
                      <span className="text-xs text-muted-foreground">
                        {selectedShape.line?.width || 2}px
                      </span>
                    </div>
                    <Slider
                      value={[selectedShape.line?.width || 2]}
                      onValueChange={([value]) => updateSelectedShape('line.width', value)}
                      min={0}
                      max={8}
                      step={0.5}
                      className="w-full"
                    />
                  </div>

                  {/* Line Style */}
                  <div>
                    <Label className="text-xs">Line Style</Label>
                    <Select 
                      value={selectedShape.line?.dash || 'solid'}
                      onValueChange={(value) => updateSelectedShape('line.dash', value)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="solid">
                          <div className="flex items-center gap-2">
                            <svg width="50" height="10">
                              <line x1="0" y1="5" x2="50" y2="5" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                            <span>Solid</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="dash">
                          <div className="flex items-center gap-2">
                            <svg width="50" height="10">
                              <line x1="0" y1="5" x2="50" y2="5" stroke="currentColor" strokeWidth="2" strokeDasharray="8,4"/>
                            </svg>
                            <span>Dashed</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="dot">
                          <div className="flex items-center gap-2">
                            <svg width="50" height="10">
                              <line x1="0" y1="5" x2="50" y2="5" stroke="currentColor" strokeWidth="2" strokeDasharray="2,3"/>
                            </svg>
                            <span>Dotted</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="dashdot">
                          <div className="flex items-center gap-2">
                            <svg width="50" height="10">
                              <line x1="0" y1="5" x2="50" y2="5" stroke="currentColor" strokeWidth="2" strokeDasharray="8,3,2,3"/>
                            </svg>
                            <span>Dash-Dot</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="longdash">
                          <div className="flex items-center gap-2">
                            <svg width="50" height="10">
                              <line x1="0" y1="5" x2="50" y2="5" stroke="currentColor" strokeWidth="2" strokeDasharray="12,6"/>
                            </svg>
                            <span>Long Dash</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="longdashdot">
                          <div className="flex items-center gap-2">
                            <svg width="50" height="10">
                              <line x1="0" y1="5" x2="50" y2="5" stroke="currentColor" strokeWidth="2" strokeDasharray="12,4,2,4"/>
                            </svg>
                            <span>Long Dash-Dot</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Fill for rect/circle */}
                  {(selectedShape.type === 'rect' || selectedShape.type === 'circle') && (
                    <>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="fill" className="text-sm">Fill Shape</Label>
                        <Switch
                          id="fill"
                          checked={selectedShape.fillcolor !== 'rgba(0, 0, 0, 0)' && selectedShape.fillcolor !== 'transparent'}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              updateSelectedShape('fillcolor', 'rgba(0, 0, 0, 0.1)');
                              updateSelectedShape('opacity', 1);
                            } else {
                              updateSelectedShape('fillcolor', 'rgba(0, 0, 0, 0)');
                            }
                          }}
                        />
                      </div>

                      {selectedShape.fillcolor !== 'rgba(0, 0, 0, 0)' && selectedShape.fillcolor !== 'transparent' && (
                        <>
                          <ColorPicker
                            label="Fill Color"
                            value={selectedShape.fillcolor || 'rgba(0, 0, 0, 0.1)'}
                            onChange={(color) => updateSelectedShape('fillcolor', color)}
                            includeOpacity={false}
                          />
                          
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <Label className="text-xs">Opacity</Label>
                              <span className="text-xs text-muted-foreground">
                                {Math.round((selectedShape.opacity || 1) * 100)}%
                              </span>
                            </div>
                            <Slider
                              value={[(selectedShape.opacity || 1) * 100]}
                              onValueChange={([value]) => updateSelectedShape('opacity', value / 100)}
                              min={0}
                              max={100}
                              step={5}
                              className="w-full"
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {!selectedShape && !annotations.some(isArrow) && shapes.length === 0 && (
            <div className="text-center py-4 text-sm text-muted-foreground">
              Add a shape or arrow to get started
            </div>
          )}
      </div>
    </div>
  );
}
